import React from 'react';
import { Minus, Plus } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { Talent } from '../../services/api';

export interface TalentSelection {
  talent: Talent;
  selected: boolean;
  antall: number;
  beskrivelse: string;
}

interface SelectableTalentListProps {
  selections: TalentSelection[];
  onToggle: (index: number) => void;
  onAntallChange: (index: number, antall: number) => void;
  onBeskrivelseChange: (index: number, beskrivelse: string) => void;
}

const SelectableTalentList: React.FC<SelectableTalentListProps> = ({
  selections,
  onToggle,
  onAntallChange,
  onBeskrivelseChange,
}) => {
  return (
    <div className="mt-2">
      {selections.map((selection, index) => (
        <div
          key={selection.talent.id}
          className={cn(
            'flex flex-col gap-2 p-3 rounded-md mb-1 transition-all',
            selection.selected
              ? 'bg-accent border-2 border-primary'
              : 'bg-muted hover:bg-muted/70 hover:shadow-sm'
          )}
        >
          {/* Første rad: Checkbox og navn */}
          <div className="flex items-center gap-2">
            <Checkbox
              checked={selection.selected}
              onCheckedChange={() => onToggle(index)}
            />
            <span className={cn('flex-grow text-sm', selection.selected ? 'font-semibold' : 'font-normal')}>
              {selection.talent.navn}
            </span>
          </div>

          {/* Andre rad: Antall og beskrivelse (kun hvis valgt) */}
          {selection.selected && (
            <div className="flex gap-4 pl-8 flex-wrap items-start">
              {/* Antall-velger */}
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 bg-background"
                  onClick={() => onAntallChange(index, selection.antall - 1)}
                  disabled={selection.antall <= 1}
                >
                  <Minus className="h-3.5 w-3.5" />
                </Button>
                <div className="relative w-[90px]">
                  <Input
                    type="number"
                    min={1}
                    value={selection.antall}
                    onChange={(e) => onAntallChange(index, parseInt(e.target.value, 10) || 1)}
                    className="h-8 text-center font-semibold pr-8"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                    stk
                  </span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 bg-background"
                  onClick={() => onAntallChange(index, selection.antall + 1)}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>

              {/* Beskrivelse */}
              <Textarea
                placeholder="Beskrivelse (valgfri)"
                value={selection.beskrivelse}
                onChange={(e) => onBeskrivelseChange(index, e.target.value)}
                rows={1}
                className="flex-1 min-w-[200px] resize-none"
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default SelectableTalentList;
